import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DateTime } from "luxon";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import {
  collection,
  doc,
  type DocumentSnapshot,
  endBefore,
  getDoc,
  getDocs,
  type QueryDocumentSnapshot,
  limit,
  limitToLast,
  onSnapshot,
  or,
  orderBy,
  query,
  startAfter,
  startAt,
  where,
} from "firebase/firestore";
import { Link, useParams } from "react-router-dom";
import { type TestRun, type TestSuite } from "@/utils/types";
import { customFirestore } from "@/firebase";
import { AuthContext } from "@/auth/AuthContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { BACKEND_SERVER_URL } from "@/constants/aws-constants";
import { CancelOutlined, Loop } from "@mui/icons-material";
import { callCancelSuiteAPI, trackSentryException } from "@/utils/helpers";
import { useSetAtom } from "jotai";
import { customAlertStateAtom } from "@/components/custom-alert";
import {
  DataGridPro,
  type GridColDef,
  type GridDataSource,
  type GridGetRowsParams,
  type GridRowId,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { GridDataSourceCacheDefault } from "@mui/x-data-grid/hooks/features/dataSource";

// MUI DataGrid default dimensions — used to pre-compute detail panel height
// so the grid never has to measure it with ResizeObserver (avoids scroll jumps).
const DETAIL_ROW_HEIGHT = 52; // DataGrid default rowHeight
const DETAIL_HEADER_HEIGHT = 56; // DataGrid default columnHeaderHeight

// ---------------------------------------------------------------------------
// Run-count helper
// ---------------------------------------------------------------------------

interface RunCounts {
  scheduledCount: number;
  runningCount: number;
  succeededCount: number;
  failedCount: number;
  blockedCount: number;
  cancelledCount: number;
  totalCount: number;
}

// Statuses where the suite (and its runs) will never change again.
const FINAL_STATUSES = new Set(["completed", "cancelled"]);

const ZERO_COUNTS: RunCounts = {
  scheduledCount: 0,
  runningCount: 0,
  succeededCount: 0,
  failedCount: 0,
  blockedCount: 0,
  cancelledCount: 0,
  totalCount: 0,
};

const computeRunCounts = (docs: QueryDocumentSnapshot[]): RunCounts =>
  docs.reduce(
    (acc, doc) => {
      const data = doc.data();
      const s = data.status as string;
      // A run auto-classified as a legitimate app issue is conceptually a
      // failure (the app misbehaved, not the test agent). Count it as failed
      // even if the server has moved the run out of the "failed" status bucket.
      const isAppIssue =
        data.finalFailureClassification === "legitimate_app_issue";
      if (s === "initial") acc.scheduledCount++;
      if (s === "running") acc.runningCount++;
      if (s === "succeeded" && !isAppIssue) acc.succeededCount++;
      if (s === "failed" || isAppIssue) acc.failedCount++;
      if (s === "blocked") acc.blockedCount++;
      if (s === "cancelled") acc.cancelledCount++;
      acc.totalCount++;
      return acc;
    },
    { ...ZERO_COUNTS }
  );

// ---------------------------------------------------------------------------
// Inline cell components (main grid)
// ---------------------------------------------------------------------------

const FailedCountCell = ({
  params,
  onRetry,
}: {
  params: any;
  onRetry: (row: TestSuite, status: string) => Promise<void>;
}) => {
  const value = Number(params.value ?? 0);
  const status: string = params.row?.status ?? "";
  const canRetry = value > 0 && ["completed", "cancelled"].includes(status);

  return (
    <span className="text-[#FF4C4C] flex items-center gap-1">
      {value > 0 ? `${value} Test${value > 1 ? "s" : ""}` : "---"}
      {canRetry && (
        <Tooltip title="Retry failed tests">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              void onRetry(params.row as TestSuite, "failed");
            }}
          >
            <Loop fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </span>
  );
};

const BlockedCountCell = ({
  params,
  onRetry,
}: {
  params: any;
  onRetry: (row: TestSuite, status: string) => Promise<void>;
}) => {
  const value = Number(params.value ?? 0);
  const status: string = params.row?.status ?? "";
  const canRetry = value > 0 && ["completed", "cancelled"].includes(status);

  return (
    <span className="text-orange-400 flex items-center gap-1">
      {canRetry ? `${value} Test${value > 1 ? "s" : ""}` : "---"}
      {canRetry && (
        <Tooltip title="Retry blocked tests">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              void onRetry(params.row as TestSuite, "blocked");
            }}
          >
            <Loop fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </span>
  );
};

const StatusCell = ({
  params,
  onCancel,
}: {
  params: any;
  onCancel: (suiteId: string) => Promise<void>;
}) => {
  const status: string = params.value ?? "";

  return (
    <span className="flex items-center gap-1">
      {status}
      {status === "running" && (
        <Tooltip title="Cancel whole test suite">
          <IconButton
            size="small"
            sx={{ marginBlock: "-7px" }}
            onClick={(e) => {
              e.stopPropagation();
              void onCancel(String(params.row.id));
            }}
          >
            <CancelOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Status label / colour maps shared by the detail grid
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<string, string> = {
  failed: "Failed",
  succeeded: "Succeeded",
  running: "In Progress",
  initial: "Scheduled",
  blocked: "Blocked",
  cancelled: "Cancelled",
};

const STATUS_CLASS: Record<string, string> = {
  failed: "text-[#FF4C4C]",
  succeeded: "text-[#17BD27]",
  running: "text-[#7C7C7C]",
  initial: "text-[#7C7C7C]",
  blocked: "text-orange-400",
  cancelled: "text-gray-600",
};

type AutoReviewBadge = {
  label: string;
  color: "info" | "warning" | "error";
  // When true, the badge replaces the existing status pill rather than
  // appearing alongside it (used while the review is still in flight, so
  // the user doesn't see a red "Failed" pill for a state that may flip).
  replacePill?: boolean;
};

// Surfaces in-progress auto-review state on the run-list cell. While a review is
// running we hide the raw status pill (which may still flip) and show a progress
// chip instead. Terminal runs show only the plain Succeeded/Failed pill.
const getAutoReviewBadge = (run: TestRun): AutoReviewBadge | null => {
  if (!run.pendingReview) return null;
  const iterNumber = (run.reviewIterationCount ?? 0) + 1;
  const label =
    run.status === "succeeded"
      ? `Verifying success (${iterNumber}/3)…`
      : `Reviewing failure (${iterNumber}/3)…`;
  return { label, color: "info", replacePill: true };
};

// ---------------------------------------------------------------------------
// RunDetailPanel — master-detail content using a nested DataGridPro
// ---------------------------------------------------------------------------

const RUN_STATUS_ORDER: Record<string, number> = {
  running: 0,
  initial: 1,
  succeeded: 2,
  failed: 3,
  blocked: 4,
  cancelled: 5,
};

const RunDetailPanel = ({ row }: { row: TestSuite }) => {
  const [runs, setRuns] = useState<TestRun[]>([]);
  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(customFirestore, `custom-test-suite-runs/${row.id}/runs`),
      (snapshot) => {
        const newRuns = snapshot.docs.map((doc) => {
          const raw = doc.data();
          const run: TestRun = {
            ...(raw as TestRun),
            id: doc.id,
            testSuiteId: row.id,
            osVersion: raw.os as string,
            title: (raw.test_title ?? raw.title) as string,
          };
          return run;
        });
        setRuns(newRuns);
      }
    );
    return unsubscribe;
  }, [row.id]);

  const detailColumns: GridColDef[] = useMemo(
    () => [
      {
        field: "status",
        headerName: "Status",
        width: 200,
        sortComparator: (a, b) =>
          (RUN_STATUS_ORDER[a as string] ?? 99) -
          (RUN_STATUS_ORDER[b as string] ?? 99),
        renderCell: ({ row }) => {
          const run = row as TestRun;
          const s = String(run.status ?? "");
          const badge = getAutoReviewBadge(run);

          return (
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {!badge?.replacePill && (
                <span className={STATUS_CLASS[s] ?? ""}>
                  {STATUS_LABEL[s] ?? s}
                </span>
              )}
              {badge && (
                <Chip
                  size="small"
                  color={badge.color}
                  label={badge.label}
                  sx={{ height: 20 }}
                />
              )}
            </span>
          );
        },
      },
      { field: "title", headerName: "Title", flex: 1 },
      { field: "device", headerName: "Device", width: 150 },
      { field: "osVersion", headerName: "OS", width: 100 },
      { field: "locale", headerName: "Locale", width: 100 },
      {
        field: "actions",
        headerName: "",
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row: runRow }) => {
          const run = runRow as TestRun;
          if (
            run.status === "succeeded" ||
            run.status === "failed" ||
            run.status === "blocked"
          ) {
            return (
              <Link
                to={`/recording/${run.testSuiteId ?? ""}/runs/${run.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="underline hover:cursor-pointer">
                  view recording
                </span>
                <ArrowUpRightIcon className="inline w-3 h-3 ml-1 hover:cursor-pointer" />
              </Link>
            );
          }
          if (run.status === "running" || run.status === "initial") {
            return (
              <Tooltip title="Cancel single test">
                <IconButton
                  size="small"
                  sx={{ marginBlock: "-5px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    void (async () => {
                      try {
                        await callCancelSuiteAPI(true, run.testSuiteId, run.id);
                      } catch (_) {
                        setCustomAlertState({
                          open: true,
                          type: "error",
                          title: "Something went wrong",
                          description:
                            "An error occurred while canceling the test execution",
                        });
                      }
                    })();
                  }}
                >
                  <CancelOutlined />
                </IconButton>
              </Tooltip>
            );
          }
          return null;
        },
      },
    ],
    [setCustomAlertState]
  );

  return (
    <div className="px-8">
      <DataGridPro
        rows={runs}
        columns={detailColumns}
        initialState={{
          sorting: { sortModel: [{ field: "status", sort: "asc" }] },
        }}
        hideFooter
        disableColumnMenu
        sx={{
          borderRadius: 0,
          borderLeft: 0,
          borderRight: 0,
          borderTop: 0,
          borderBottom:0
          // "& .MuiDataGrid-columnHeaders": { borderTop: 0 },
          // "& .MuiDataGrid-row--lastVisible .MuiDataGrid-cell": {
          //   borderBottom: 0,
          // },
        }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// ReportsPage
// ---------------------------------------------------------------------------

const ReportsPage = () => {
  useDocumentTitle("Reports");
  const { user } = useContext<any>(AuthContext);
  const { suiteId: targetSuiteId } = useParams<{ suiteId?: string }>();
  const apiRef = useGridApiRef();
  const cursorCache = useRef(new Map<number, DocumentSnapshot>());
  const runSubscriptions = useRef(new Map<string, () => void>());
  const suiteSubscriptions = useRef(new Map<string, () => void>());
  // Tracks the topmost-loaded doc + whether more newer exist. Used to lazy-load
  // newer suites when the user scrolls up in anchored mode (the grid's native
  // lazy loading only handles downward scrolling).
  const newerCursorRef = useRef<QueryDocumentSnapshot | null>(null);
  const hasMoreNewerRef = useRef<boolean>(false);
  const loadingNewerRef = useRef<boolean>(false);
  const [gridVersion, setGridVersion] = useState(0);
  // When the URL targets a specific suite, this holds the target doc snapshot.
  // The dataSource uses it as a Firestore cursor to fetch a window of ~25
  // newer + target + ~25 older suites, so the target appears in context.
  const [anchorContext, setAnchorContext] = useState<
    { targetSnap: DocumentSnapshot } | null
  >(null);
  const [anchorResolving, setAnchorResolving] = useState(false);
  const [loadingNewer, setLoadingNewer] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(
    () => new Set<GridRowId>(targetSuiteId ? [targetSuiteId] : [])
  );
  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  useEffect(() => {
    if (!targetSuiteId) {
      setAnchorContext(null);
      setAnchorResolving(false);
      setExpandedRowIds(new Set());
      return;
    }
    setExpandedRowIds(new Set<GridRowId>([targetSuiteId]));
    setAnchorContext(null);
    setAnchorResolving(true);
    let cancelled = false;
    void (async () => {
      try {
        const snap = await getDoc(
          doc(customFirestore, "custom-test-suite-runs", targetSuiteId)
        );
        if (cancelled) return;
        if (!snap.exists()) {
          setCustomAlertState({
            open: true,
            type: "error",
            title: "Suite not found",
            description: `No report exists for suite ${targetSuiteId}.`,
          });
          return;
        }
        setAnchorContext({ targetSnap: snap });
      } catch (e) {
        trackSentryException(e);
      } finally {
        if (!cancelled) setAnchorResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetSuiteId, setCustomAlertState]);

  // Anchor changes require the grid to discard cached pages and re-fetch.
  useEffect(() => {
    newerCursorRef.current = null;
    hasMoreNewerRef.current = false;
    loadingNewerRef.current = false;
    setGridVersion((v) => v + 1);
  }, [anchorContext]);

  // Once the target row has loaded into the grid, scroll it into view.
  useEffect(() => {
    if (!targetSuiteId || anchorContext == null) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let elapsed = 0;
    const tick = () => {
      const api = apiRef.current;
      if (api?.getRow?.(targetSuiteId) != null) {
        const idx = api.getRowIndexRelativeToVisibleRows?.(targetSuiteId);
        if (typeof idx === "number" && idx >= 0) {
          api.scrollToIndexes?.({ rowIndex: idx });
        }
        return;
      }
      elapsed += 150;
      if (elapsed < 5000) timer = setTimeout(tick, 150);
    };
    timer = setTimeout(tick, 150);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [targetSuiteId, anchorContext, apiRef, gridVersion]);

  const resetLiveSuiteSubscriptions = useCallback(() => {
    runSubscriptions.current.forEach((unsub) => {
      unsub();
    });
    runSubscriptions.current.clear();

    suiteSubscriptions.current.forEach((unsub) => {
      unsub();
    });
    suiteSubscriptions.current.clear();

    cursorCache.current.clear();
  }, []);

  // Clean up all subscriptions on unmount.
  useEffect(() => {
    return resetLiveSuiteSubscriptions;
  }, [resetLiveSuiteSubscriptions]);

  useEffect(() => {
    // When viewing a deep-linked suite, suppress the auto-reset on new suites
    // so the target's row index (= newerCount) stays accurate.
    if (anchorContext != null) return;

    const latestSuiteQuery = query(
      collection(customFirestore, "custom-test-suite-runs"),
      or(
        where("organizationId", "==", user.companyId),
        where("organisationId", "==", user.companyId)
      ),
      orderBy("timeInEpoch", "desc"),
      limit(1)
    );

    let isInitialSnapshot = true;
    return onSnapshot(latestSuiteQuery, (snapshot) => {
      if (isInitialSnapshot) {
        isInitialSnapshot = false;
        return;
      }

      const hasNewSuite = snapshot
        .docChanges()
        .some((change) => change.type === "added");

      if (!hasNewSuite) {
        return;
      }

      resetLiveSuiteSubscriptions();
      setGridVersion((current) => current + 1);
    });
  }, [resetLiveSuiteSubscriptions, user.companyId, anchorContext]);

  const retryTests = useCallback(async (row: TestSuite, status: string) => {
    try {
      const url = `${BACKEND_SERVER_URL}/retrySuite`;
      const body = {
        organisationId: row.organisationId,
        testSuiteId: row.id,
        status,
      };
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (response.status !== 200) {
        console.error("Failed to retry suite", response);
        return;
      }

      // The suite was in a final status — re-attach live listeners now that
      // it's running again so the row gets real-time count and status updates.

      // Run-level listener: keep counts up to date.
      runSubscriptions.current.get(row.id)?.();
      const runsUnsub = onSnapshot(
        collection(customFirestore, `custom-test-suite-runs/${row.id}/runs`),
        (runSnap) => {
          apiRef.current?.updateRows([
            { id: row.id, ...computeRunCounts(runSnap.docs) },
          ]);
        }
      );
      runSubscriptions.current.set(row.id, runsUnsub);

      // Suite-level listener: track status changes and tear down when final.
      suiteSubscriptions.current.get(row.id)?.();
      const suiteUnsub = onSnapshot(
        doc(customFirestore, `custom-test-suite-runs/${row.id}`),
        (docSnap) => {
          if (!docSnap.exists()) return;
          const data = docSnap.data();
          apiRef.current?.updateRows([{ id: row.id, ...data }]);

          if (FINAL_STATUSES.has(data.status as string)) {
            runSubscriptions.current.get(row.id)?.();
            runSubscriptions.current.delete(row.id);
            suiteSubscriptions.current.get(row.id)?.();
            suiteSubscriptions.current.delete(row.id);
          }
        }
      );
      suiteSubscriptions.current.set(row.id, suiteUnsub);
    } catch (error) {
      trackSentryException(error);
      console.error("Error:", error);
    }
  }, []);

  const cancelSuite = useCallback(
    async (suiteId: string) => {
      try {
        await callCancelSuiteAPI(false, suiteId);
      } catch (_) {
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Something went wrong",
          description: "An error occurred while canceling the suite execution",
        });
      }
    },
    [setCustomAlertState]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "timeInEpoch",
        headerName: "Time",
        width: 200,
        filterable: false,
        sortable: false,
        renderCell: ({ value }) =>
          value != null
            ? DateTime.fromMillis(Number(value) * 1000).toFormat(
                "yyyy-MM-dd hh:mm a"
              )
            : "---",
      },
      {
        field: "trigger",
        headerName: "Trigger",
        width: 88,
        filterable: false,
        sortable: false,
      },
      {
        field: "appVersionName",
        headerName: "App Version",
        width: 256,
        filterable: false,
        sortable: false,
      },
      {
        field: "totalCount",
        headerName: "# Tests",
        width: 100,
        filterable: false,
        sortable: false,
        renderCell: ({ value }) => {
          const n = Number(value ?? 0);
          return n > 0 ? `${n} Test${n > 1 ? "s" : ""}` : "---";
        },
      },
      {
        field: "succeededCount",
        headerName: "# Succeeded",
        width: 120,
        filterable: false,
        sortable: false,
        renderCell: ({ value }) => {
          const n = Number(value ?? 0);
          return (
            <span className="text-[#17BD27]">
              {n > 0 ? `${n} Test${n > 1 ? "s" : ""}` : "---"}
            </span>
          );
        },
      },
      {
        field: "failedCount",
        headerName: "# Failed",
        width: 120,
        filterable: false,
        sortable: false,
        renderCell: (params) => (
          <FailedCountCell params={params} onRetry={retryTests} />
        ),
      },
      {
        field: "blockedCount",
        headerName: "# Blocked",
        width: 120,
        filterable: false,
        sortable: false,
        renderCell: (params) => (
          <BlockedCountCell params={params} onRetry={retryTests} />
        ),
      },
      {
        field: "cancelledCount",
        headerName: "# Cancelled",
        width: 120,
        filterable: false,
        sortable: false,
        renderCell: ({ value }) => {
          const n = Number(value ?? 0);
          return (
            <span className="text-gray-600">
              {n > 0 ? `${n} Test${n > 1 ? "s" : ""}` : "---"}
            </span>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        filterable: false,
        sortable: false,
        renderCell: (params) => (
          <StatusCell params={params} onCancel={cancelSuite} />
        ),
      },
    ],
    [retryTests, cancelSuite]
  );

  const dataSourceCache = useMemo(
    () => new GridDataSourceCacheDefault({ ttl: 1000 * 60 * 5 }),
    [gridVersion]
  );

  // Builds grid rows for a set of suite docs: pulls run counts (one-shot for
  // final suites, live-subscribed for active ones) and attaches a status
  // listener that tears down its own subscriptions when the suite finalises.
  // Shared between the dataSource's getRows and the scroll-up "load newer" path.
  const buildRowsForDocs = useCallback(
    async (docs: QueryDocumentSnapshot[]) => {
      const suites: TestSuite[] = docs
        .filter((d) => user.role === "superUser" || !d.data().stealthMode)
        .map((d) => {
          const raw = { id: d.id, ...d.data() };
          return raw as unknown as TestSuite;
        });

      const counts = await Promise.all(
        suites.map(async (suite) => {
          if (FINAL_STATUSES.has(suite.status)) {
            const snap = await getDocs(
              collection(
                customFirestore,
                `custom-test-suite-runs/${suite.id}/runs`
              )
            );
            return computeRunCounts(snap.docs);
          }

          return await new Promise<RunCounts>((resolve) => {
            runSubscriptions.current.get(suite.id)?.();
            let firstEmit = true;
            const unsub = onSnapshot(
              collection(
                customFirestore,
                `custom-test-suite-runs/${suite.id}/runs`
              ),
              (runSnap) => {
                const c = computeRunCounts(runSnap.docs);
                if (firstEmit) {
                  firstEmit = false;
                  resolve(c);
                } else {
                  apiRef.current?.updateRows([{ id: suite.id, ...c }]);
                }
              },
              () => {
                if (firstEmit) {
                  firstEmit = false;
                  resolve({ ...ZERO_COUNTS });
                }
              }
            );
            runSubscriptions.current.set(suite.id, unsub);
          });
        })
      );

      const rows = suites.map((s, i) => ({ ...s, ...counts[i] }));

      suites.forEach((suite) => {
        if (FINAL_STATUSES.has(suite.status)) return;
        if (suiteSubscriptions.current.has(suite.id)) return;

        const unsub = onSnapshot(
          doc(customFirestore, `custom-test-suite-runs/${suite.id}`),
          (docSnap) => {
            if (!docSnap.exists()) return;
            const data = docSnap.data();
            apiRef.current?.updateRows([{ id: suite.id, ...data }]);
            if (FINAL_STATUSES.has(data.status as string)) {
              runSubscriptions.current.get(suite.id)?.();
              runSubscriptions.current.delete(suite.id);
              suiteSubscriptions.current.get(suite.id)?.();
              suiteSubscriptions.current.delete(suite.id);
            }
          }
        );
        suiteSubscriptions.current.set(suite.id, unsub);
      });

      return rows;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiRef is stable
    [user.companyId, user.role]
  );

  const dataSource: GridDataSource = useMemo(
    () => ({
      getRows: async (params: GridGetRowsParams) => {
        const start = params.start as number;
        const end = params.end;
        const pageSize = end - start;

        if (start === 0) {
          cursorCache.current.clear();
        }

        const baseFilters = or(
          where("organizationId", "==", user.companyId),
          where("organisationId", "==", user.companyId)
        );

        // Default (non-anchored) view: page through suites and hide ones that
        // finished with no runs. Because empties are dropped, a single fetch can
        // return a page that is entirely (or mostly) empty — that is what
        // blanked the screen when the newest suites had no runs. The fill loop
        // below keeps pulling batches until it has a full page of visible rows
        // (or Firestore runs out). The cursor cache is keyed by VISIBLE-row
        // count: in lazy-loading/infinite mode the grid requests the next page
        // at `start = rows already loaded`, so we stash the last consumed doc
        // under `start + visibleRows.length` for that lookup.
        if (anchorContext == null) {
          let cursor: DocumentSnapshot | undefined =
            start > 0 ? cursorCache.current.get(start) : undefined;
          if (start > 0 && !cursor) {
            return { rows: [], rowCount: start };
          }

          const visibleRows: Awaited<ReturnType<typeof buildRowsForDocs>> = [];
          let exhausted = false;

          while (visibleRows.length < pageSize && !exhausted) {
            // Only fetch the shortfall (+1 probe) each pass so the expensive
            // per-suite run-count reads stay bounded even when scanning past a
            // long run of empty suites.
            const remaining = pageSize - visibleRows.length;
            let q = query(
              collection(customFirestore, "custom-test-suite-runs"),
              baseFilters,
              orderBy("timeInEpoch", "desc"),
              limit(remaining + 1)
            );
            if (cursor) {
              q = query(q, startAfter(cursor));
            }

            const snapshot = await getDocs(q);
            const hasMore = snapshot.docs.length > remaining;
            const batchDocs = hasMore
              ? snapshot.docs.slice(0, remaining)
              : snapshot.docs;
            exhausted = !hasMore;

            if (batchDocs.length === 0) break;

            const built = await buildRowsForDocs(batchDocs);
            for (let i = 0; i < built.length; i += 1) {
              cursor = batchDocs[i];
              const row = built[i];
              const isEmptyFinal =
                Number(row.totalCount ?? 0) === 0 &&
                FINAL_STATUSES.has(row.status);
              if (!isEmptyFinal) {
                visibleRows.push(row);
                if (visibleRows.length === pageSize) break;
              }
            }
          }

          if (cursor && visibleRows.length > 0) {
            cursorCache.current.set(start + visibleRows.length, cursor);
          }

          return {
            rows: visibleRows,
            rowCount: exhausted ? start + visibleRows.length : undefined,
          };
        }

        // Anchored deep-link view (/reports/:suiteId): unchanged — shows the
        // target suite in context (empties included).
        let docs: QueryDocumentSnapshot[];
        let hasMore: boolean;

        // Windowed deep-link load: up to (pageSize/2) newer + target + older,
        // capped to exactly pageSize rows so the grid's lazy-loading
        // pagination stays consistent and can continue fetching older pages.
        // Uses the target snap as a Firestore cursor — no extra index needed.
        if (start === 0) {
          const newerLimit = Math.floor(pageSize / 2);
          const targetSnap = anchorContext.targetSnap;
          const [newerSnap, lowerSnap] = await Promise.all([
            getDocs(
              query(
                collection(customFirestore, "custom-test-suite-runs"),
                baseFilters,
                orderBy("timeInEpoch", "desc"),
                endBefore(targetSnap),
                limitToLast(newerLimit)
              )
            ),
            getDocs(
              query(
                collection(customFirestore, "custom-test-suite-runs"),
                baseFilters,
                orderBy("timeInEpoch", "desc"),
                startAt(targetSnap),
                limit(pageSize + 1)
              )
            ),
          ]);
          const newerDocs = newerSnap.docs;
          const remainingSlots = pageSize - newerDocs.length;
          const trimmedLowerCount = Math.min(
            remainingSlots,
            lowerSnap.docs.length
          );
          const lowerDocs = lowerSnap.docs.slice(0, trimmedLowerCount);
          hasMore = lowerSnap.docs.length > trimmedLowerCount;
          docs = [...newerDocs, ...lowerDocs];

          // Set up the newer-side cursor so scrolling up can continue loading.
          newerCursorRef.current = newerDocs.length > 0 ? newerDocs[0] : null;
          hasMoreNewerRef.current = newerDocs.length === newerLimit;
        } else {
          let q = query(
            collection(customFirestore, "custom-test-suite-runs"),
            baseFilters,
            orderBy("timeInEpoch", "desc"),
            limit(pageSize + 1)
          );

          if (start > 0) {
            const cursor = cursorCache.current.get(start);
            if (!cursor) {
              return { rows: [], rowCount: start };
            }
            q = query(q, startAfter(cursor));
          }

          const snapshot = await getDocs(q);
          hasMore = snapshot.docs.length > pageSize;
          docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;
        }

        if (docs.length > 0) {
          docs.forEach((d, i) => {
            cursorCache.current.set(start + i + 1, d);
          });
        }

        const rows = await buildRowsForDocs(docs);

        return {
          rows,
          rowCount: hasMore ? undefined : start + rows.length,
        };
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiRef is a stable ref from useGridApiRef
    [user.companyId, user.role, anchorContext, buildRowsForDocs]
  );

  // Prepends the next batch of suites newer than the topmost loaded doc, then
  // adjusts the scroll position so the user's current view stays put. Called
  // by the scroll listener when the user nears the top of an anchored view.
  const loadMoreNewer = useCallback(async () => {
    if (loadingNewerRef.current || !hasMoreNewerRef.current) return;
    const cursor = newerCursorRef.current;
    const api = apiRef.current;
    if (!cursor || !api) return;

    loadingNewerRef.current = true;
    setLoadingNewer(true);
    try {
      const BATCH = 50;
      const snap = await getDocs(
        query(
          collection(customFirestore, "custom-test-suite-runs"),
          or(
            where("organizationId", "==", user.companyId),
            where("organisationId", "==", user.companyId)
          ),
          orderBy("timeInEpoch", "desc"),
          endBefore(cursor),
          limitToLast(BATCH)
        )
      );
      const newerDocs = snap.docs;
      hasMoreNewerRef.current = newerDocs.length === BATCH;
      if (newerDocs.length === 0) return;
      newerCursorRef.current = newerDocs[0];

      const newRows = await buildRowsForDocs(newerDocs);
      if (newRows.length === 0) return;

      const scrollBefore = api.getScrollPosition?.() ?? { top: 0, left: 0 };

      // Shift existing cursor positions to make room for the prepended rows.
      const shifted = new Map<number, DocumentSnapshot>();
      cursorCache.current.forEach((cur, pos) => {
        shifted.set(pos + newerDocs.length, cur);
      });
      newerDocs.forEach((d, i) => {
        shifted.set(i + 1, d);
      });
      cursorCache.current = shifted;

      const currentRows = Array.from(api.getRowModels().values()) as any[];
      api.setRows([...newRows, ...currentRows]);

      // Compensate for prepended rows so the user's viewport stays anchored
      // to the row they were looking at.
      const ROW_HEIGHT = 52;
      requestAnimationFrame(() => {
        api.scroll?.({
          top: scrollBefore.top + newRows.length * ROW_HEIGHT,
          left: scrollBefore.left,
        });
      });
    } catch (e) {
      trackSentryException(e);
    } finally {
      loadingNewerRef.current = false;
      setLoadingNewer(false);
    }
  }, [apiRef, user.companyId, buildRowsForDocs]);

  // Detect upward scrolling near the top of an anchored view and trigger a
  // newer-side load. Native lazyLoading only fires for downward scrolling.
  useEffect(() => {
    if (anchorContext == null) return;
    const api = apiRef.current;
    if (!api?.subscribeEvent) return;
    const SCROLL_TRIGGER_PX = 200;
    return api.subscribeEvent("scrollPositionChange", (params: any) => {
      const top = params?.top ?? 0;
      if (top < SCROLL_TRIGGER_PX) void loadMoreNewer();
    });
  }, [apiRef, anchorContext, gridVersion, loadMoreNewer]);

  return (
    <div className="flex-1 flex flex-col bg-[#E6E6E6] h-full min-w-0">
      <div className="flex flex-row flex-1 h-[calc(100vh_-_60px)] min-w-0">
        <div className="flex flex-1 gap-3 w-full bg-[#D9D9D9] p-4 min-w-0">
          <div className="flex flex-1 flex-col bg-[#E8E8E8] h-full p-4 rounded-[8px] gap-8 min-w-0 overflow-hidden">
            <div className="flex items-baseline justify-between gap-4">
              <p className="text-[24px] font-medium">Reports</p>
              {targetSuiteId && (
                <Link
                  to="/gpt-driver/reports"
                  className="text-sm underline text-[#4C6EF5]"
                >
                  ← Back to all reports
                </Link>
              )}
            </div>
            {anchorResolving ? (
              <div className="flex flex-1 items-center justify-center">
                <CircularProgress />
              </div>
            ) : (
              <div
                style={{
                  flex: 1,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                {loadingNewer && (
                  <LinearProgress
                    sx={{
                      position: "absolute",
                      top: DETAIL_HEADER_HEIGHT,
                      left: 0,
                      right: 0,
                      zIndex: 5,
                      height: 3,
                    }}
                  />
                )}
                <DataGridPro
                key={gridVersion}
                apiRef={apiRef}
                columns={columns}
                dataSource={dataSource}
                dataSourceCache={dataSourceCache}
                lazyLoading
                initialState={{
                  pagination: { paginationModel: { pageSize: 50 } },
                }}
                detailPanelExpandedRowIds={expandedRowIds}
                onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
                getRowClassName={({ id }) =>
                  targetSuiteId && id === targetSuiteId
                    ? "highlighted-suite-row"
                    : ""
                }
                getDetailPanelContent={({ row }) => (
                  <RunDetailPanel row={row as TestSuite} />
                )}
                getDetailPanelHeight={({ row }) => {
                  const count = Number(row.totalCount ?? 0);
                  return (
                    DETAIL_HEADER_HEIGHT +
                    Math.max(count, 1) * DETAIL_ROW_HEIGHT
                  );
                }}
                sx={{
                  flex: 1,
                  "& .highlighted-suite-row": {
                    backgroundColor: "rgba(76, 110, 245, 0.14)",
                    boxShadow: "inset 3px 0 0 0 #4C6EF5",
                  },
                  "& .highlighted-suite-row:hover, & .highlighted-suite-row.Mui-hovered":
                    {
                      backgroundColor: "rgba(76, 110, 245, 0.22) !important",
                    },
                }}
              />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
